import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import { buildDynamicQrValue, hexEpochSeconds } from '@/lib/dynamic-qr';

interface LiveTicketQrProps {
  /** Raw ONDC authorization token (the static block). */
  staticBlock: string;
  /** Static non-zero coordinates embedded in the dynamic block. */
  lat: string;
  lng: string;
  /** Client regen cadence (seconds); kept well under the gate's 30s window. */
  refreshSeconds: number;
  /**
   * serverClock − clientClock (ms). Corrects a skewed device clock so the
   * emitted timestamp tracks real (gate) time rather than the phone's clock.
   */
  offsetMs: number;
  /** Text of the green "Live" pill (e.g. "Live & valid" or "Live"). */
  liveLabel?: string;
  /** Mono hint shown under the QR (defaults to the "it changes on its own" copy). */
  hint?: ReactNode;
}

/**
 * LiveTicketQr renders the BMRCL gate QR framed as a printed artifact — crop-mark
 * corners on warm paper (no white box), a pulsing "Live" pill, a `refreshes m:ss`
 * countdown, a depleting green hairline, and a brightness flash on each rotation.
 *
 * The dynamic block regenerates every `refreshSeconds`, entirely client-side (no
 * network) so it keeps working at an underground gate once the ticket is open. The
 * timestamp is offset-corrected to the server clock; EC level "L" keeps the (long)
 * symbol sparse for easy scanning off a backlit screen. The QR is drawn in the near-
 * black `--qr-fg` on a transparent background so the paper shows through.
 */
export function LiveTicketQr({
  staticBlock,
  lat,
  lng,
  refreshSeconds,
  offsetMs,
  liveLabel = 'Live & valid',
  hint,
}: LiveTicketQrProps) {
  const period = Math.max(1, refreshSeconds);

  // Keep the latest offset in a ref so a new poll updates it without resetting
  // the rotation cycle (the effect only re-subscribes when token/coords/period change).
  const offsetRef = useRef(offsetMs);
  offsetRef.current = offsetMs;

  const [value, setValue] = useState(() =>
    buildDynamicQrValue(staticBlock, hexEpochSeconds(Date.now() + offsetMs), lat, lng)
  );
  const [remaining, setRemaining] = useState(period);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let start = Date.now();
    const rotate = () => {
      setValue(
        buildDynamicQrValue(staticBlock, hexEpochSeconds(Date.now() + offsetRef.current), lat, lng)
      );
      // Retrigger the brightness flash so the re-scramble reads as "current", not a glitch.
      const el = imgRef.current;
      if (el) {
        el.classList.remove('flash');
        void el.offsetWidth; // reflow to restart the animation
        el.classList.add('flash');
      }
    };
    rotate(); // refresh immediately on (re)mount or when the token/coords change
    start = Date.now();
    setRemaining(period);

    const id = window.setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      setRemaining(Math.max(0, Math.ceil(period - elapsed)));
      if (elapsed >= period) {
        start = Date.now();
        rotate();
      }
    }, 500);

    // A backgrounded tab pauses timers; refresh on return so the QR is never stale.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        start = Date.now();
        setRemaining(period);
        rotate();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [staticBlock, lat, lng, period]);

  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <div className="qrblock">
      <div className="live">
        <span className="pill t-good">
          <span className="dot pulse" />
          {liveLabel}
        </span>
        <span className="cd">
          refreshes {mm}:{ss}
        </span>
      </div>
      <div className="crop">
        <span className="ck" />
        <span className="ck" />
        <span className="ck" />
        <span className="ck" />
        <div className="qrimg" ref={imgRef}>
          <QRCodeSVG
            value={value}
            size={256}
            level="L"
            marginSize={2}
            bgColor="transparent"
            fgColor="#17130d"
          />
        </div>
      </div>
      <div className="depl">
        <i style={{ animationDuration: `${period}s` }} />
      </div>
      <div className="qrhint">{hint ?? "Code changes on its own — that's normal"}</div>
    </div>
  );
}
